import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { transactionFormSchema, TransactionFormData } from '@/lib/schemas/forms';
import { Category } from '@/lib/api';
import { toDateString, fromDateString, formatDateShort } from '@/lib/format';
import { CalendarIcon, Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TransactionFormProps {
  categories: Category[];
  onSubmit: (data: TransactionFormData) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  defaultValues?: Partial<TransactionFormData>;
  submitLabel?: string;
}

export const TransactionForm: React.FC<TransactionFormProps> = ({
  categories,
  onSubmit,
  onCancel,
  isSubmitting = false,
  defaultValues,
  submitLabel = 'Adicionar',
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TransactionFormData>({
    resolver: zodResolver(transactionFormSchema),
    defaultValues: {
      amount: '',
      category_id: '',
      date: toDateString(new Date()),
      description: '',
      type: undefined,
      ...defaultValues,
    },
  });

  const selectedType = watch('type');
  const selectedDate = watch('date');
  const filteredCategories = selectedType
    ? categories.filter((c) => c.type === selectedType)
    : categories;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Tipo</Label>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={selectedType === 'income' ? 'default' : 'outline'}
              className={cn(
                selectedType === 'income' && 'bg-income hover:bg-income/90'
              )}
              onClick={() => {
                setValue('type', 'income');
                setValue('category_id', '');
              }}
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Receita
            </Button>
            <Button
              type="button"
              variant={selectedType === 'expense' ? 'default' : 'outline'}
              className={cn(
                selectedType === 'expense' && 'bg-expense hover:bg-expense/90'
              )}
              onClick={() => {
                setValue('type', 'expense');
                setValue('category_id', '');
              }}
            >
              <TrendingDown className="h-4 w-4 mr-2" />
              Despesa
            </Button>
          </div>
          {errors.type && (
            <p className="text-xs text-destructive">{errors.type.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>Valor</Label>
          <CurrencyInput
            placeholder="0,00"
            {...register('amount')}
            className={errors.amount ? 'border-destructive' : ''}
          />
          {errors.amount && (
            <p className="text-xs text-destructive">{errors.amount.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>Categoria</Label>
          <Select
            value={watch('category_id')}
            onValueChange={(value) => setValue('category_id', value)}
            disabled={!selectedType}
          >
            <SelectTrigger className={errors.category_id ? 'border-destructive' : ''}>
              <SelectValue placeholder={selectedType ? 'Selecione...' : 'Selecione o tipo primeiro'} />
            </SelectTrigger>
            <SelectContent>
              {filteredCategories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.category_id && (
            <p className="text-xs text-destructive">{errors.category_id.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>Data</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  errors.date && 'border-destructive'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? formatDateShort(selectedDate) : 'Selecione...'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate ? fromDateString(selectedDate) || undefined : undefined}
                onSelect={(date) => date && setValue('date', toDateString(date))}
                initialFocus
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
          {errors.date && (
            <p className="text-xs text-destructive">{errors.date.message}</p>
          )}
        </div>

        <div className="space-y-2 sm:col-span-2">
          <Label>Descrição</Label>
          <Input
            placeholder="Descrição da transação"
            {...register('description')}
            className={errors.description ? 'border-destructive' : ''}
          />
          {errors.description && (
            <p className="text-xs text-destructive">{errors.description.message}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="gradient-primary text-primary-foreground"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Salvando...
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </form>
  );
};
